import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/table/data-table";
import { InviteModal, SuccessModal } from "../../components/modal";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const LEAVE_TYPES_CACHE_KEY = "cehr_company_leave_types_v1";

const LeaveTypes = () => {
  const cachedData = useMemo(
    () => readSessionCache(LEAVE_TYPES_CACHE_KEY, { leaveTypes: [] }),
    [],
  );
  const [openLeaveTypeModal, setOpenLeaveTypeModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState(cachedData?.leaveTypes || []);
  const [leaveTypeName, setLeaveTypeName] = useState("");
  const [leaveTypeDescription, setLeaveTypeDescription] = useState("");
  const [numberOfDays, setNumberOfDays] = useState("");
  const [durationUnit, setDurationUnit] = useState("DAYS");
  const [isLoading, setIsLoading] = useState(!cachedData?.leaveTypes?.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const apiBaseUrl = getApiBaseUrl();
  const navigate = useNavigate();

  const loadLeaveTypes = useCallback(
    async ({ silent = false } = {}) => {
      if (!apiBaseUrl) {
        setErrorMessage("API base URL is not configured.");
        return;
      }

      try {
        if (!silent && leaveTypes.length === 0) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(
          `${apiBaseUrl}/organizations/leave-types`,
        );
        const nextLeaveTypes = response?.data?.leaveTypes || [];
        setLeaveTypes(nextLeaveTypes);
        writeSessionCache(LEAVE_TYPES_CACHE_KEY, {
          leaveTypes: nextLeaveTypes,
        });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message ||
          "Unable to load leave types. Please try again.";
        setErrorMessage(
          Array.isArray(responseMessage)
            ? responseMessage.join(", ")
            : responseMessage,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl, leaveTypes.length],
  );

  useEffect(() => {
    loadLeaveTypes({ silent: true });
  }, [loadLeaveTypes]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadLeaveTypes({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadLeaveTypes]);

  const openModal = () => setOpenLeaveTypeModal(true);

  const resetForm = () => {
    setLeaveTypeName("");
    setLeaveTypeDescription("");
    setNumberOfDays("");
    setDurationUnit("DAYS");
    setErrorMessage("");
  };

  const closeModal = () => {
    setOpenLeaveTypeModal(false);
    resetForm();
  };

  const createHandleSuccess = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    const parsedDuration = Number(numberOfDays);

    if (
      !leaveTypeName.trim() ||
      !leaveTypeDescription.trim() ||
      !numberOfDays.trim()
    ) {
      setErrorMessage(
        "Leave type name, description and duration are required.",
      );
      return;
    }

    if (!Number.isInteger(parsedDuration) || parsedDuration < 1) {
      setErrorMessage("Duration must be a whole number greater than 0.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.post(`${apiBaseUrl}/organizations/leave-types`, {
        leaveTypeName: leaveTypeName.trim(),
        leaveTypeDescription: leaveTypeDescription.trim(),
        numberOfDays: durationUnit === "WEEKS" ? parsedDuration * 7 : parsedDuration,
        durationUnit,
      });

      closeModal();
      setOpenSuccessModal(true);
      await loadLeaveTypes();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to create leave type. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const headings = [
    "Leave Type Name",
    "Description",
    "Duration",
    "Actions",
  ];
  const columns = ["leaveTypeName", "leaveTypeDescription", "durationDisplay"];
  const displayedLeaveTypes = leaveTypes.map((leaveType) => {
    const unit = leaveType.durationUnit === "WEEKS" ? "WEEKS" : "DAYS";
    const value = unit === "WEEKS" ? leaveType.numberOfDays / 7 : leaveType.numberOfDays;
    return {
      ...leaveType,
      durationDisplay: `${value} ${unit === "WEEKS" ? (value === 1 ? "week" : "weeks") : (value === 1 ? "day" : "days")}`,
    };
  });

  return (
    <div className="w-full h-full p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Leave Types" },
        ]}
        title="Leave Types"
        subtitle="Define leave categories and set their allowance in days or weeks."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            onClick={openModal}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        }
      />

      <DataTable
        title=""
        headings={headings}
        columns={columns}
        data={displayedLeaveTypes}
        showEmptyState={!isLoading}
        emptyMessage="No leave types found. Create one to get started."
        onView={(row) => {
          sessionStorage.setItem("selectedLeaveTypeId", row.id);
          navigate("/company-settings/view-details", {
            state: { leaveTypeId: row.id },
          });
        }}
      />

      <InviteModal
        isOpen={openLeaveTypeModal}
        onClose={closeModal}
      >
        <div className="my-8">
          <div>
            <p className="text-2xl font-semibold text-black font-Nunito">
              Create Leave Type
            </p>
          </div>
          <form
            className="my-12"
            onSubmit={createHandleSuccess}
          >
            {errorMessage && (
              <p
                className="mb-4 text-sm text-red-600"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="leave-type-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Leave Type Name
              </label>
              <input
                type="text"
                id="leave-type-name"
                placeholder="Title"
                value={leaveTypeName}
                onChange={(ev) => setLeaveTypeName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="leave-type-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Description
              </label>
              <textarea
                id="leave-type-description"
                placeholder="Description"
                value={leaveTypeDescription}
                onChange={(ev) => setLeaveTypeDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-24 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="leave-duration"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Duration
              </label>
              <div className="mb-4 grid grid-cols-[1fr_140px] gap-3">
                <input
                  type="number"
                  id="leave-duration"
                  placeholder={durationUnit === "WEEKS" ? "e.g. 2" : "e.g. 15"}
                  value={numberOfDays}
                  onChange={(ev) => setNumberOfDays(ev.target.value)}
                  min="1"
                  step="1"
                  className="w-full p-2 text-center rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                />
                <select
                  value={durationUnit}
                  onChange={(ev) => setDurationUnit(ev.target.value)}
                  aria-label="Leave duration unit"
                  className="w-full rounded-xl border-2 border-[#F3F5F5] bg-white px-3 focus:outline-[#1234CD]"
                >
                  <option value="DAYS">Days</option>
                  <option value="WEEKS">Weeks</option>
                </select>
              </div>
            </div>

            <div className="flex justify-center mt-20 items-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] font-Nunito leading-[21.6px] w-40 rounded-xl text-white py-[16px] bg-[#1234CD] h-[40px]text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>{isSubmitting ? "Creating..." : "Create"}</span>
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />

      {isLoading && leaveTypes.length === 0 ? (
        <FetchLoadingState message="Loading leave types..." />
      ) : null}
    </div>
  );
};

export default LeaveTypes;
